import IdentificationTypeSelect from '@/components/common/input/identification-types-select/IdentificationTypeSelect';
import CalendarModal from '@/components/common/modals/calendar-modal/CalendarModal';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import formCampusVeranoWindsurfareaSchema from '@/schemas/form-campus-verano-windsurfarea';
import { useRootStore } from '@/stores/root-store';
import { zodResolver } from '@hookform/resolvers/zod';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import FormCampusVeranoWindsurfareaProps, {
  TForm
} from './FormCampusVeranoWindsurfarea.types';
import { Button } from '@/components/ui/button';
import { Loader } from 'lucide-react';

const CampusVeranoWindsurfarea: React.FC<FormCampusVeranoWindsurfareaProps> = ({
  onSubmit,
  isPending
}) => {
  const { t } = useTranslation();
  const stepServicesData = useRootStore((store) => store.stepServicesData);
  const removeStep = useRootStore((store) => store.removeStep);
  const currentStep = useRootStore((store) => store.currentStep);
  const minAge = stepServicesData?.service?.minAge;
  const maxAge = stepServicesData?.service?.maxAge;
  const form = useForm<TForm>({
    resolver: zodResolver(
      formCampusVeranoWindsurfareaSchema({ minAge, maxAge })
    )
  });
  const formError = Boolean(Object.keys(form.formState.errors).length);

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-8"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        {/* Datos del padre/madre/tutor (1) */}
        <div className="border border-primary p-6 rounded-md space-y-4">
          <h3 className="font-bold">{t('stepParticipants.titleParent')}</h3>

          {/* Nombre */}
          <FormField
            control={form.control}
            name="user.firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('stepParticipants.form.firstName')}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Apellido */}
          <FormField
            control={form.control}
            name="user.lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('stepParticipants.form.lastName')}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Teléfono */}
          <FormField
            control={form.control}
            name="user.phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('phone')}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="user.identification.type"
            render={({ field }) => {
              return (
                <FormItem>
                  <IdentificationTypeSelect
                    defaultValue={field?.value}
                    onValueChange={(value) => {
                      // Mixpanel.track('press user id input', {
                      //   screen: 'New Booking',
                      //   organization: 'Viento en popa'
                      // });
                      field.onChange(value);
                    }}
                    field={undefined}
                  />
                  <FormMessage />
                </FormItem>
              );
            }}
          />
          <FormField
            control={form.control}
            name="user.identification.number"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormLabel>
                    {t('stepParticipants.form.identificationNumber')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      // onClick={() =>
                      //   Mixpanel.track(
                      //     'press participants id number input',
                      //     {
                      //       screen: 'New Booking',
                      //       organization: 'Viento en popa'
                      //     }
                      //   )
                      // }
                      placeholder={t('stepParticipants.form.id')}
                      {...field}
                      value={field?.value || ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          {/* Email */}
          <FormField
            control={form.control}
            name="user.email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('stepParticipants.form.mail')}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormDescription>
                  {t('stepParticipants.form.mail_description')}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Domicilio */}
          <FormField
            control={form.control}
            name="user.address"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('form.address')}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Localidad */}
          <FormField
            control={form.control}
            name="user.location"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('common.location')}</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Madrid" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* CP */}
          <FormField
            control={form.control}
            name="user.postalCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('common.postalCode')}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Datos del padre/madre/tutor (2) */}
        <div className="border border-primary p-6 rounded-md space-y-4">
          <h3 className="font-bold">
            {t('stepParticipants.titleParent')} {t('form.optional')}
          </h3>

          {/* Nombre */}
          <FormField
            control={form.control}
            name="parent.firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('stepParticipants.form.firstName')}</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Introduce el nombre" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Apellido */}
          <FormField
            control={form.control}
            name="parent.lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('stepParticipants.form.lastName')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Introduce el segundo apellido"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Teléfono */}
          <FormField
            control={form.control}
            name="parent.phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('phone')}</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Introduce el teléfono" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="parent.identification.type"
            render={({ field }) => {
              return (
                <FormItem>
                  <IdentificationTypeSelect
                    defaultValue={field?.value}
                    onValueChange={(value) => {
                      // Mixpanel.track('press user id input', {
                      //   screen: 'New Booking',
                      //   organization: 'Viento en popa'
                      // });
                      field.onChange(value);
                    }}
                    field={undefined}
                  />
                  <FormMessage />
                </FormItem>
              );
            }}
          />
          <FormField
            control={form.control}
            name="parent.identification.number"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormLabel>
                    {t('stepParticipants.form.identificationNumber')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      // onClick={() =>
                      //   Mixpanel.track(
                      //     'press participants id number input',
                      //     {
                      //       screen: 'New Booking',
                      //       organization: 'Viento en popa'
                      //     }
                      //   )
                      // }
                      placeholder={t('stepParticipants.form.id')}
                      {...field}
                      value={field?.value || ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          {/* Email */}
          <FormField
            control={form.control}
            name="parent.email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('stepParticipants.form.mail')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Introduce el correo electrónico"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Domicilio */}
          <FormField
            control={form.control}
            name="parent.address"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('form.address')}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Localidad */}
          <FormField
            control={form.control}
            name="parent.location"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('common.location')}</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Madrid" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* CP */}
          <FormField
            control={form.control}
            name="parent.postalCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('common.postalCode')}</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Introduce su CP" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Datos del alumno */}
        <div className="border border-primary p-6 rounded-md space-y-4">
          <h3 className="font-bold">{t('stepParticipants.titleStudent')}</h3>

          {/* Nombre */}
          <FormField
            control={form.control}
            name="student.firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('stepParticipants.form.firstName')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Introduce el nombre del alumno"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Primer Apellido */}
          <FormField
            control={form.control}
            name="student.lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('stepParticipants.form.lastName')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Introduce el apellido del alumno"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="student.identification.type"
            render={({ field }) => {
              return (
                <FormItem>
                  <IdentificationTypeSelect
                    defaultValue={field?.value}
                    onValueChange={(value) => {
                      // Mixpanel.track('press user id input', {
                      //   screen: 'New Booking',
                      //   organization: 'Viento en popa'
                      // });
                      field.onChange(value);
                    }}
                    field={undefined}
                  />
                  <FormMessage />
                </FormItem>
              );
            }}
          />
          <FormField
            control={form.control}
            name="student.identification.number"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormLabel>
                    {t('stepParticipants.form.identificationNumber')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('stepParticipants.form.id')}
                      {...field}
                      value={field?.value || ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          {/* Fecha de Nacimiento */}
          <FormField
            control={form.control}
            name="student.birthdate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Fecha de Nacimiento</FormLabel>
                <CalendarModal
                  value={field.value}
                  placeholder={t('stepParticipants.form.birth')}
                  onAccept={(value) => {
                    field.onChange(value);
                  }}
                  // disabledInputLabel={disabled}
                  // onClickInputLabel={() =>
                  //   Mixpanel.track('press birthday date button ', {
                  //     screen: 'New Booking'
                  //   })
                  // }
                  initialFocus
                  dropDownPicker
                />
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Enfermedad */}
          <FormField
            control={form.control}
            name="student.illness"
            render={({ field }) => {
              return (
                <FormItem>
                  <div className="flex flex-row items-end gap-5">
                    <FormLabel>{t('stepParticipants.illness')}</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={(value) =>
                          field.onChange({
                            ...field.value,
                            value: value === 'yes'
                          })
                        }
                        defaultValue=""
                        className="flex space-x-2 mt-0 items-center"
                      >
                        <FormItem className="flex items-center space-x-1 space-y-0">
                          <FormControl>
                            <RadioGroupItem value="yes" />
                          </FormControl>
                          <FormLabel className="font-normal">
                            {t('form.yes')}
                          </FormLabel>
                        </FormItem>
                        <FormItem className="flex items-center space-x-1 space-y-0">
                          <FormControl>
                            <RadioGroupItem value="no" />
                          </FormControl>
                          <FormLabel className="font-normal">
                            {t('form.no')}
                          </FormLabel>
                        </FormItem>
                      </RadioGroup>
                    </FormControl>
                  </div>

                  {field?.value?.value && (
                    <div className="px-2 py-1">
                      <Textarea
                        placeholder={t('newBooking.extraInfo')}
                        {...field}
                        onChange={(e) =>
                          field.onChange({
                            ...field.value,
                            answer: e.target.value
                          })
                        }
                        value={field?.value?.answer || ''}
                      />
                      {/* {answerError && (
                      <p className="text-sm font-medium text-destructive">
                        {answerError.message}
                      </p>
                    )} */}
                    </div>
                  )}
                </FormItem>
              );
            }}
          />
        </div>

        <div>
          <p className="text-xs font-medium">
            {t('stepParticipants.form.responsibility')}{' '}
            <a
              href="https://windsurfarea.com/escuela-nautica-reglamento/"
              target="blank"
              className="text-primary underline"
              rel="noreferrer noopener"
            >
              {t('stepParticipants.form.responsibilityLink')}
            </a>
          </p>
        </div>

        {/* Privacidad de datos  */}
        <FormField
          control={form.control}
          name="acceptDataPrivacy"
          render={({ field }) => (
            <FormItem>
              <div className="flex flex-start">
                <FormControl>
                  <Checkbox
                    id="isAcceptDataPrivacy"
                    checked={field.value}
                    onCheckedChange={() => field.onChange(!field.value)}
                  />
                </FormControl>
                <FormLabel htmlFor="isAcceptDataPrivacy" className="ml-2">
                  {t('stepParticipants.form.privacyPolicy')}{' '}
                  <a
                    href="https://windsurfarea.com/politica-privacidad/"
                    target="blank"
                    className="text-primary underline"
                    rel="noreferrer noopener"
                  >
                    {t('stepParticipants.form.privacyPolicyLink')}
                  </a>
                </FormLabel>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Fotografía  */}
        <FormField
          control={form.control}
          name="acceptTakePhotos"
          render={({ field }) => (
            <FormItem>
              <div className="flex flex-start">
                <FormControl>
                  <Checkbox
                    id="isAcceptTakePhotos"
                    checked={field.value}
                    onCheckedChange={() => field.onChange(!field.value)}
                  />
                </FormControl>
                <FormLabel htmlFor="isAcceptTakePhotos" className="ml-2">
                  {t('stepParticipants.form.takePhotos')}
                </FormLabel>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Ofrecimiento de productos/servicios  */}
        <FormField
          control={form.control}
          name="authOfferProducts"
          render={({ field }) => (
            <FormItem>
              <div className="flex flex-start">
                <FormControl>
                  <Checkbox
                    id="isAuthOfferProducts"
                    checked={field.value}
                    onCheckedChange={() => field.onChange(!field.value)}
                  />
                </FormControl>
                <FormLabel htmlFor="isAuthOfferProducts" className="ml-2">
                  {t('stepParticipants.form.acceptOffers')}
                </FormLabel>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        {formError && (
          <p className="text-sm font-medium text-destructive">
            {t('newBooking.formParticipantData.errorMessage')}
          </p>
        )}

        <div className="flex flex-col gap-3">
          <Button type="submit" className="w-full" disabled={isPending}>
            {isPending ? (
              <Loader className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              t('newBooking.continue')
            )}
          </Button>
          <Button
            disabled={isPending}
            onClick={() => {
              removeStep(currentStep);
            }}
            variant={'outline'}
          >
            {t('newBooking.goBack')}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default CampusVeranoWindsurfarea;
